import React, { useEffect, useState } from 'react';
import appSlider from 'assets/images/app-slider.mp4';
import columnImage2 from 'assets/images/column-image2.png';
import columnImage2Webp from 'assets/images/column-image2.png?as=webp';
import columnImage3 from 'assets/images/column-image3.png';
import columnImage3Webp from 'assets/images/column-image3.png?as=webp';
import columnImage4 from 'assets/images/column-image4.png';
import columnImage4Webp from 'assets/images/column-image4.png?as=webp';
import columnImage5 from 'assets/images/column-image5.png';
import columnImage5Webp from 'assets/images/column-image5.png?as=webp';
import columnImage7 from 'assets/images/column-image7.png';
import columnImage7Webp from 'assets/images/column-image7.png?as=webp';
import columnImage8 from 'assets/images/column-image8.jpg';
import columnImage8Webp from 'assets/images/column-image8.jpg?as=webp';
import columnImage9 from 'assets/images/column-image9.jpg';
import columnImage9Webp from 'assets/images/column-image9.jpg?as=webp';
import columnImage10 from 'assets/images/column-image10.png';
import columnImage10Webp from 'assets/images/column-image10.png?as=webp';
import filterExploded from 'assets/images/filter-exploded.png';
import filterExplodedWebp from 'assets/images/filter-exploded.png?as=webp';
import LazyLoad from 'react-lazyload';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import loadable from '@loadable/component';
import { SUPPORTED_REGIONS } from '../../../shared/enums';
import { getPerfectSquare } from '../../../shared/utils';
import { selectLocalizationData } from '../../features/localization/localizationSlice';

const FadeIn = loadable(() => import('../common/FadeIn'));

/**
 * @returns {JSX.Element}
 * @constructor
 */
const MediaBlock = () => {
  const localization = useSelector(selectLocalizationData);
  const measure = [ SUPPORTED_REGIONS.US, SUPPORTED_REGIONS.CA ].includes(localization.region) ? 'ft' : 'm';
  const [ isImgLoading, setIsImgLoading ] = useState(false);
  const [ awaitImgLoad, setAwaitImgLoad ] = useState(false);

  useEffect(() => {
    if (!isImgLoading) return;
    const timer = setTimeout(() => {
      setAwaitImgLoad(true);
    }, 500);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [ isImgLoading ]);

  return (
    <section className='media-block'>
      <div className='container'>
        <div className='media-block__item media-block--reverse media-block--style1'>
          <figure className='media-block__video img-relative' style={{ aspectRatio: !awaitImgLoad ? `${540 / 807}` : 'auto' }}>
            <LazyLoad>
              <FadeIn>
                <video
                  id='media-block__slider-video'
                  playsInline
                  webkit-playsinline='true'
                  autoPlay
                  muted
                  loop
                  onLoadedData={() => setIsImgLoading(true)}
                >
                  <source src={appSlider} type='video/mp4' />
                </video>
              </FadeIn>
            </LazyLoad>
          </figure>
          <div className='media-block__content'>
            <h2 className='media-block__title'>You never know if an air purifier is actually working.</h2>
            <div className='media-block__description'>
              <p>
                Mila is the first purifier to show you how much she’s cleaned. Every time you adjust the fan speed,
                you’ll see the expected air quality and time to achieve it.
              </p>
              <p>It’s as simple as setting your thermostat.</p>

            </div>
          </div>
        </div>
        <div className='media-block__item media-block--style2'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={columnImage2Webp} type='image/webp' />
                  <source srcSet={columnImage2} type='image/png' />
                  <img src={columnImage2} alt='Living room with mila' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>You need a PhD to figure out what size to buy.</h2>
            <div className='media-block__description'>
              <p>
                Mila is the right size for every room. She learns the size of each space to give you the same level of
                performance, whether she’s placed in a
                {' '}
                {getPerfectSquare(150, localization.region, measure)}
                {' '}
                {measure}
                <sup>2</sup>
                {' '}
                nursery or a
                {' '}
                {getPerfectSquare(400, localization.region, measure)}
                {' '}
                {measure}
                <sup>2</sup>
                {' '}
                family room.
              </p>
            </div>
          </div>
        </div>
        <div className='media-block__item media-block--reverse media-block--style3'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={columnImage3Webp} type='image/webp' />
                  <source srcSet={columnImage3} type='image/png' />
                  <img src={columnImage3} alt='Multiple Mila filters' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>They market unproven filtration “technology.”</h2>
            <div className='media-block__description'>
              <p>
                Mila uses certified HEPA, the gold standard in fine particle filtration. She also rocks a Clean Air
                Delivery Rate (CADR) of 460 m<sup>3</sup>
                /hr (that's more than 2x-5x that of competitors like Dyson and
                Molekule).
              </p>
            </div>
          </div>
        </div>
        <div className='media-block__item media-block--style4'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={columnImage4Webp} type='image/webp' />
                  <source srcSet={columnImage4} type='image/png' />
                  <img src={columnImage4} alt='Mila features info' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>They’re noisy.</h2>
            <div className='media-block__description'>
              <p>
                Mila is smart enough not to disturb you. She's the first purifier to detect room presence, so she knows
                to quiet down when you enter the room and deep clean when you’re gone.
              </p>
            </div>
          </div>
        </div>
        <div className='media-block__item media-block--reverse media-block--style5'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={columnImage5Webp} type='image/webp' />
                  <source srcSet={columnImage5} type='image/png' />
                  <img src={columnImage5} alt='Mila in white' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>They’re ugly.</h2>
            <div className='media-block__description'>
              <p>
                Mila is cute. 🤗
              </p>
            </div>
          </div>
        </div>
        <div className='media-block__item media-block--style6'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={filterExplodedWebp} type='image/webp' />
                  <source srcSet={filterExploded} type='image/png' />
                  <img src={filterExploded} alt='carbon filter' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>They cheat and call a few ounces of carbon dust a “carbon filter.”</h2>
            <div className='media-block__description'>
              <p>
                Filtration of gases depends on the weight and depth of activated carbon. Mila’s patent-pending Dual-Flow
                filter holds over 1 lb of granular active carbon in a 3-inch bed, giving you best-in-class filtration for
                household odors and harmful VOCs from paints, carpets, and pressed woods.
              </p>
            </div>
          </div>
        </div>
        <div className='media-block__item media-block--reverse media-block--style7'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={columnImage7Webp} type='image/webp' />
                  <source srcSet={columnImage7} type='image/png' />
                  <img src={columnImage7} alt='mila filter boxes' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>
              They include one filter that tries to do a little bit of everything—but
              isn't good at anything.
            </h2>
            <div className='media-block__description'>
              <p>
                Mila lets you choose from
                {' '}
                <Link to='/filters'>7 custom filters</Link>
                , each purposefully designed to solve a
                particular home health need —such as pet dander or home renovation dust and chemicals.
              </p>
            </div>
          </div>
        </div>
        <div className='media-block__item media-block--style8'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={columnImage8Webp} type='image/webp' />
                  <source srcSet={columnImage8} type='image/jpg' />
                  <img src={columnImage8} alt='blender circa 1984' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>They come with 3 antiquated speeds —like a blender circa 1984.</h2>
            <div className='media-block__description'>
              <p>
                Mila is far more thoughtful and smart. Her Automagic Mode makes thousands of calculations in the
                background to determine optimal settings based on room type, time of day, sensor readings, and your
                personal preferences.
              </p>
            </div>
          </div>
        </div>
        <div className='media-block__item media-block--reverse media-block--style9'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={columnImage9Webp} type='image/webp' />
                  <source srcSet={columnImage9} type='image/jpg' />
                  <img src={columnImage9} alt='Mila app home screenshot' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>They hide behind confusing industry lingo.</h2>
            <div className='media-block__description'>
              <p>
                Mila translates the complex science of air quality
                into plain language, filling you in using terms you
                can understand.
              </p>
            </div>
          </div>
        </div>
        <div className='media-block__item media-block--style10'>
          <div className='media-block__image'>
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={columnImage10Webp} type='image/webp' />
                  <source srcSet={columnImage10} type='image/png' />
                  <img src={columnImage10} alt='Mila performance chart' />
                </picture>
              </FadeIn>
            </LazyLoad>
          </div>
          <div className='media-block__content'>
            <h2 className='media-block__title'>
              They’re priced like Ferraris and perform like golf carts. 🚗
            </h2>
            <div className='media-block__description'>
              <p>
                Mila delivers twice the value at half the price.
                Doesn’t everyone deserve to breathe clean air?
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MediaBlock;
